import { datadogLogs } from '@datadog/browser-logs';
import type { RumErrorEvent } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import { addReactError, reactPlugin } from '@datadog/browser-rum-react';
import type { ErrorInfo } from 'react';

import { envVars, WORKER_MAIN_PROXY_PATH } from '@/lib/environment/config';
import { reporter } from '@infinex-private/ui-error-reporter';
import type { ErrorBoundaryContext } from '@infinex-private/ui-react-error-boundary';

const isDatadogEnabled =
  envVars.DATADOG_RUM_APPLICATION_ID && envVars.DATADOG_RUM_CLIENT_TOKEN;

// Proxy DD requests through the connect worker to bypass ad blockers.
// Requests go: browser → /main-proxy/_dduba/... → main worker → Datadog intake.
const ddProxyBase = `${WORKER_MAIN_PROXY_PATH}/_dduba`;

// Declared in vite.config.ts
declare const APP_VERSION: string;

reporter.init({
  reportError: (error, context) => {
    if (!isDatadogEnabled) {
      console.error('[Datadog disabled] Error:', error, context);
      return;
    }

    datadogRum.addError(error, context);
  },

  onBoundaryError: (
    error: Error,
    errorInfo: ErrorInfo,
    context: ErrorBoundaryContext
  ) => {
    if (!isDatadogEnabled) {
      console.error(
        '[Datadog disabled] Boundary error:',
        error,
        errorInfo,
        context
      );
      return;
    }

    const { isFatal, ...additionalContext } = context;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Datadog handles dd_context internally
    (error as any).dd_context = {
      is_crash: isFatal,
      ...additionalContext,
    };

    addReactError(error, errorInfo);
  },
});

if (isDatadogEnabled) {
  // Initialize Logs SDK to forward console logs to RUM sessions
  datadogLogs.init({
    clientToken: envVars.DATADOG_RUM_CLIENT_TOKEN!,
    site: envVars.DATADOG_RUM_SITE ?? 'us5.datadoghq.com',
    service: 'connect-app',
    env: envVars.INFINEX_ENVIRONMENT,
    version: APP_VERSION,
    forwardConsoleLogs: ['log', 'info', 'warn', 'error'],
    forwardErrorsToLogs: true,
    proxy: ({ path, parameters }) => `${ddProxyBase}${path}?${parameters}`,
  });

  datadogRum.init({
    applicationId: envVars.DATADOG_RUM_APPLICATION_ID!,
    clientToken: envVars.DATADOG_RUM_CLIENT_TOKEN!,
    site: envVars.DATADOG_RUM_SITE ?? 'us5.datadoghq.com',
    service: 'connect-app',
    env: envVars.INFINEX_ENVIRONMENT,
    version: APP_VERSION,
    // DD RUM will not work on local dev with allowedTracingUrls set
    allowedTracingUrls: [/^https:\/\/[^/]+\.infinex\.xyz/],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask-user-input',
    // Prevent session sharing across subdomains - each popup gets its own session
    trackSessionAcrossSubdomains: false,
    plugins: [reactPlugin({ router: true })],
    beforeSend: (event) => {
      if (event.type === 'error') {
        return includeError(event);
      }

      return true;
    },
    proxy: ({ path, parameters }) => `${ddProxyBase}${path}?${parameters}`,
  });
}

function includeError(event: RumErrorEvent): boolean {
  const stack = event.error.stack;

  if (!stack) {
    return true;
  }

  // Filter out errors from known browser extensions
  return (
    !stack.includes('fldfpgipfncgndfolcbkdeeknbbbnhcc') &&
    !stack.includes('onhogfjeacnfoofkfgppdlbmlmnplgbn') &&
    !stack.includes('fcfcfllfndlomdhbehjjcoimbgofdncg')
  );
}
